import { isNotDefined } from './typescript-helpers.ts';

/**
 * Parses a number string according to the given locale.
 * @param value The string to parse
 * @param locale The locale to use for parsing (default: 'en-US')
 * @returns The parsed number, undefined if value is empty, or NaN if invalid
 * @example
 * parseLocaleNumber('1,234.56', 'en-US'); // 1234.56
 * parseLocaleNumber('1\u202f234,56', 'fr-FR'); // 1234.56
 * parseLocaleNumber('abc', 'en-US'); // NaN
 * parseLocaleNumber('', 'en-US'); // undefined
 */
export function parseLocaleNumber(value: string, locale = 'en-US') {
  if (value === '') {
    return undefined;
  }

  if (!value || !/\d/.test(value)) {
    return Number.NaN;
  }

  const formatter = new Intl.NumberFormat(locale);
  const parts = formatter.formatToParts(12345.6);
  const group = parts.find((p) => p.type === 'group')?.value || ' ';
  const decimal = parts.find((p) => p.type === 'decimal')?.value || '.';

  return Number(
    value
      .replace(/\s/g, '') // remove spaces
      .replace(new RegExp('\\' + group, 'g'), '') // remove group separators
      .replace(decimal, '.'), // replace decimal separator
  );
}

export type FormatLocaleNumberOptions = {
  locale: string;
  isIntegerOnly?: boolean;
  isNegativeAllowed?: boolean;
  maximumFractionDigits?: number;
};

/**
 * Formats a number according to the given locale and options.
 * @param value The number to format
 * @param options FormatLocaleNumberOptions (custom options, locale required)
 * @returns The formatted string or '' if value is undefined
 * @example
 * formatLocaleNumber(1234.56, { locale: 'en-US' }); // '1,234.56'
 * formatLocaleNumber(1234.56, { locale: 'fr-FR' }); // '1\u202f234,56'
 * formatLocaleNumber(undefined, { locale: 'en-US' }); // ''
 * formatLocaleNumber(1234.567, { locale: 'en-US', maximumFractionDigits: 1 }); // '1,234.6'
 * formatLocaleNumber(-1234.56, { locale: 'en-US', isNegativeAllowed: false }); // '1,234.56'
 * formatLocaleNumber(1234.56, { locale: 'en-US', isIntegerOnly: true }); // '1,235'
 */
export function formatLocaleNumber(value: number | undefined, options: FormatLocaleNumberOptions) {
  if (isNotDefined(value)) {
    return '';
  }

  const { locale, isIntegerOnly, isNegativeAllowed, maximumFractionDigits } = options;

  const integerOnly = isIntegerOnly;
  const allowNegative = isNegativeAllowed !== false;
  const maxDecimals = maximumFractionDigits ?? 2;

  const intlOptions: Intl.NumberFormatOptions = {
    maximumFractionDigits: integerOnly ? 0 : maxDecimals,
    minimumFractionDigits: integerOnly ? 0 : undefined,
  };

  const numericValue = !allowNegative && value < 0 ? -value : value;

  const formatter = new Intl.NumberFormat(locale, intlOptions);

  return formatter.format(numericValue);
}

/**
 * Reformats a number string to a target locale using the provided options.
 * @param value The string to reformat
 * @param options FormatLocaleNumberOptions (custom options, locale required)
 * @returns The formatted string or '' if invalid
 * @example
 * reformatLocaleNumber('1\u202f234,56', { locale: 'en-US' }); // '1,234.56'
 * reformatLocaleNumber('1,234.56', { locale: 'fr-FR' }); // '1\u202f234,56'
 * reformatLocaleNumber('', { locale: 'en-US' }); // ''
 * reformatLocaleNumber('abc', { locale: 'en-US' }); // ''
 */
export function reformatLocaleNumber(value: string, options: FormatLocaleNumberOptions) {
  if (!value || !/\d/.test(value)) {
    return '';
  }

  const parsed = parseLocaleNumber(value, options.locale);

  if (typeof parsed !== 'number' || Number.isNaN(parsed)) {
    return '';
  }

  return formatLocaleNumber(parsed, options);
}

/**
 * Returns the decimal and group separators for a given locale.
 * @param locale The locale to inspect
 * @returns An object with decimalSeparator and groupSeparator
 * @example
 * getSeparatorsFromLocale('en-US'); // { decimalSeparator: '.', groupSeparator: ',' }
 * getSeparatorsFromLocale('fr-FR'); // { decimalSeparator: ',', groupSeparator: '\u202f' }
 */
export function getSeparatorsFromLocale(locale: string) {
  const numberWithGroupAndDecimalSeparator = 1000.1;

  const parts = Intl.NumberFormat(locale).formatToParts(numberWithGroupAndDecimalSeparator);
  const groupSeparator = parts.find((part) => part.type === 'group')?.value || ',';
  const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value || '.';

  return { decimalSeparator, groupSeparator };
}

// Example usage:
// console.log(parseLocaleNumber('134,56', 'fr-FR')); // 1234.56
// console.log(parseLocaleNumber('1,234.56', 'en-US')); // 1234.56
